import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Users, HardHat, Activity, Plus, Trash2, Copy, Check, RefreshCw,
  AlertCircle, MapPin, Clock, Loader2, Hammer, X, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'live', label: 'Live', icon: Activity },
  { key: 'workers', label: 'Ouvriers', icon: Users },
  { key: 'chantiers', label: 'Chantiers', icon: HardHat },
];

const TYPE_OPTIONS = [
  { v: 'toiture', l: 'Toiture' },
  { v: 'photovoltaique', l: 'Photovoltaïque' },
  { v: 'depannage', l: 'Dépannage / SAV' },
  { v: 'autre', l: 'Autre' },
];

const Team = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('live');
  const [config, setConfig] = useState(null);
  const [copied, setCopied] = useState(false);

  // workers
  const [workers, setWorkers] = useState([]);
  const [newWorker, setNewWorker] = useState({ name: '', role: 'ouvrier', hourly_cost: '' });
  const [pinModal, setPinModal] = useState(null); // {name, pin}

  // chantiers
  const [chantiers, setChantiers] = useState([]);
  const [showChForm, setShowChForm] = useState(false);
  const blankChantier = {
    title: '', client_name: '', address: '', gps_lat: '', gps_lng: '',
    estimated_hours: '', assigned_worker_ids: [], chef_id: '',
    type_chantier: 'toiture', date_debut: '', notes: '',
    working_days: [0, 1, 2, 3, 4], exceptional_dates: [],
  };
  const [chForm, setChForm] = useState(blankChantier);
  const [newExDate, setNewExDate] = useState('');

  // dashboard
  const [dash, setDash] = useState(null);

  const [moduleDisabled, setModuleDisabled] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [c, w, ch, d] = await Promise.all([
        api.get('/api/team/config'),
        api.get('/api/team/workers'),
        api.get('/api/team/chantiers'),
        api.get('/api/team/dashboard'),
      ]);
      setConfig(c.data);
      setWorkers(w.data?.workers || []);
      setChantiers(ch.data?.chantiers || []);
      setDash(d.data);
      setModuleDisabled(false);
    } catch (e) {
      if (e.response?.status === 403) setModuleDisabled(true);
    }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (tab !== 'live') return;
    const id = setInterval(() => {
      api.get('/api/team/dashboard').then(r => setDash(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [tab, api]);

  const copyShareLink = async () => {
    const origin = window.location.origin;
    const full = `${origin}${config?.pointage_url_path}`;
    await navigator.clipboard.writeText(full);
    setCopied(true);
    toast.success('Lien copié — envoie-le par SMS/WhatsApp');
    setTimeout(() => setCopied(false), 2200);
  };

  const createWorker = async () => {
    if (!newWorker.name.trim()) { toast.error('Nom requis'); return; }
    try {
      const r = await api.post('/api/team/workers', {
        ...newWorker,
        hourly_cost: parseFloat(newWorker.hourly_cost) || null,
      });
      setPinModal({ name: r.data.name, pin: r.data.pin });
      setNewWorker({ name: '', role: 'ouvrier', hourly_cost: '' });
      loadAll();
    } catch { toast.error('Création impossible'); }
  };

  const regenPin = async (w) => {
    if (!confirm(`Régénérer le PIN de ${w.name} ? L'ancien sera invalide.`)) return;
    try {
      const r = await api.put(`/api/team/workers/${w.id}`, { regenerate_pin: true });
      setPinModal({ name: w.name, pin: r.data.pin });
    } catch { toast.error('Échec'); }
  };

  const resetDevice = async (w) => {
    if (!confirm(`Réinitialiser l'appareil de ${w.name} ? Il pourra se connecter depuis un nouveau téléphone à son prochain login.`)) return;
    try {
      await api.put(`/api/team/workers/${w.id}`, { reset_device: true });
      toast.success(`Appareil de ${w.name} réinitialisé`);
      loadAll();
    } catch { toast.error('Échec'); }
  };

  const toggleWorker = async (w) => {
    try {
      await api.put(`/api/team/workers/${w.id}`, { active: !w.active });
      loadAll();
    } catch { toast.error('Échec'); }
  };

  const deleteWorker = async (w) => {
    if (!confirm(`Supprimer ${w.name} ? Ses pointages restent.`)) return;
    try { await api.delete(`/api/team/workers/${w.id}`); loadAll(); }
    catch { toast.error('Échec'); }
  };

  const useCurrentLocationForChantier = () => {
    if (!navigator.geolocation) { toast.error('GPS indisponible'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setChForm(f => ({ ...f, gps_lat: pos.coords.latitude.toFixed(6), gps_lng: pos.coords.longitude.toFixed(6) })),
      () => toast.error('Autorise la géolocalisation'),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const toggleAssign = (wid) => {
    setChForm(f => ({
      ...f,
      assigned_worker_ids: f.assigned_worker_ids.includes(wid)
        ? f.assigned_worker_ids.filter(x => x !== wid)
        : [...f.assigned_worker_ids, wid],
      chef_id: f.chef_id === wid && f.assigned_worker_ids.includes(wid) ? '' : f.chef_id,
    }));
  };

  const createChantier = async () => {
    if (!chForm.title.trim()) { toast.error('Titre requis'); return; }
    if (chForm.assigned_worker_ids.length === 0) { toast.error('Affecte au moins 1 ouvrier'); return; }
    try {
      await api.post('/api/team/chantiers', {
        ...chForm,
        estimated_hours: parseFloat(chForm.estimated_hours) || 0,
        gps_lat: chForm.gps_lat ? parseFloat(chForm.gps_lat) : null,
        gps_lng: chForm.gps_lng ? parseFloat(chForm.gps_lng) : null,
        chef_id: chForm.chef_id || null,
        date_debut: chForm.date_debut || null,
      });
      toast.success('Chantier créé');
      setShowChForm(false);
      setChForm(blankChantier);
      loadAll();
    } catch { toast.error('Échec création'); }
  };

  const deleteChantier = async (c) => {
    if (!confirm(`Supprimer "${c.title}" ? Tous ses pointages seront perdus.`)) return;
    try { await api.delete(`/api/team/chantiers/${c.id}`); loadAll(); }
    catch { toast.error('Échec'); }
  };

  const updateChStatus = async (c, status) => {
    try { await api.put(`/api/team/chantiers/${c.id}`, { status }); loadAll(); }
    catch (e) {
      toast.error(e.response?.data?.detail || 'Échec', { duration: 6000 });
    }
  };

  return (
    <div data-testid="team-page" className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      {moduleDisabled ? (
        <div className="glass-card p-8 lg:p-10 text-center max-w-md mx-auto" data-testid="team-module-disabled">
          <div className="w-14 h-14 rounded-full bg-atlasCyan/15 flex items-center justify-center mx-auto mb-4">
            <Hammer className="w-7 h-7 text-atlasCyan" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Module Équipe désactivé</h2>
          <p className="text-atlasTextSecondary text-sm leading-relaxed mb-6">
            Ce module gère le pointage GPS, les checklists qualité et le contrôle horaire de ton équipe.
            Active-le dans <strong className="text-white">Profil → Modules</strong> pour l'utiliser.
          </p>
          <Button onClick={() => navigate('/profile')} className="btn-primary w-full">Aller au Profil</Button>
        </div>
      ) : (
      <>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Hammer className="w-6 h-6 text-atlasCyan" />
          <h1 className="text-3xl font-bold text-white">Équipe & Chantiers</h1>
          <span className="text-[10px] px-2 py-1 rounded-full bg-atlasCyan/15 text-atlasCyan font-medium tracking-wide">PHASE 1 · POINTAGE</span>
        </div>
        <p className="text-atlasTextSecondary">Pointage GPS, contrôle temps réel des heures, dépassement budget en live.</p>
      </div>

      {/* Lien équipe à partager */}
      {config && (
        <div className="glass-card p-4 lg:p-5 border-l-4 border-l-atlasCyan flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-atlasTextSecondary mb-1">Lien à envoyer à tes ouvriers (SMS / WhatsApp)</p>
            <p className="text-white font-mono text-sm truncate">
              {window.location.origin}{config.pointage_url_path}
            </p>
            <p className="text-[11px] text-atlasTextSecondary mt-1">
              Code équipe : <span className="text-atlasCyan font-mono font-semibold">{config.shortcode}</span> · ils entrent leur PIN à 4 chiffres
            </p>
          </div>
          <Button data-testid="team-copy-link" onClick={copyShareLink} variant="outline" className="border-atlasCyan/40 text-atlasCyan whitespace-nowrap">
            {copied ? <><Check className="w-4 h-4 mr-1" />Copié</> : <><Copy className="w-4 h-4 mr-1" />Copier le lien</>}
          </Button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-atlasBorder overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`team-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === t.key ? 'text-atlasCyan border-atlasCyan' : 'text-atlasTextSecondary border-transparent hover:text-white'}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* === LIVE === */}
      {tab === 'live' && dash && (
        <div className="space-y-6">
          {dash.forgotten_alerts && dash.forgotten_alerts.length > 0 && (
            <div data-testid="forgotten-alerts" className="glass-card p-5 border-l-4 border-l-orange-500 bg-orange-500/5">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-orange-400" />
                <h3 className="text-base font-semibold text-orange-300">Fin de pointage oubliée ({dash.forgotten_alerts.length})</h3>
              </div>
              <ul className="space-y-2">
                {dash.forgotten_alerts.map(a => (
                  <li key={`${a.worker_id}-${a.chantier_id}`} className={`p-3 rounded-lg border ${a.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}>
                    <p className="text-sm text-white"><strong>{a.worker_name}</strong> est encore pointé sur <em>"{a.chantier_title}"</em></p>
                    <p className="text-xs text-atlasTextSecondary mt-1">Pointé depuis <strong className={a.severity === 'critical' ? 'text-red-400' : 'text-orange-400'}>{a.elapsed_hours}h</strong> — appelle-le pour clôturer ou utilise le statut chantier.</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">Ouvriers en activité</p><p className="text-3xl font-bold text-atlasCyan">{dash.active_workers_count}</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">Heures cumulées aujourd'hui</p><p className="text-3xl font-bold text-white">{dash.total_hours_today} h</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">Pointages flaggés (&gt;200m)</p><p className={`text-3xl font-bold ${dash.flagged_pointages_today > 0 ? 'text-orange-400' : 'text-white'}`}>{dash.flagged_pointages_today}</p></div>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">État ouvriers temps réel</h3>
            {dash.workers_state.length === 0 ? (
              <p className="text-atlasTextSecondary text-sm">Aucun ouvrier créé. Va dans <strong className="text-white">Ouvriers</strong>.</p>
            ) : (
              <ul className="divide-y divide-atlasBorder">
                {dash.workers_state.map(w => (
                  <li key={w.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white font-medium flex items-center gap-2">{w.name}
                        {w.role === 'chef' && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-atlasCyan/15 text-atlasCyan">Chef</span>}
                      </p>
                      {w.current ? (
                        <p className="text-xs text-atlasCyan">● Sur "{w.current.chantier_title}" depuis {new Date(w.current.since).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}{w.current.flagged && <span className="text-orange-400 ml-2">⚠ hors zone</span>}</p>
                      ) : (
                        <p className="text-xs text-atlasTextSecondary">○ Inactif</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-white font-semibold">{w.hours_today} h</p>
                      <p className="text-[10px] text-atlasTextSecondary uppercase">aujourd'hui</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {dash.chantiers_state.length > 0 && (
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Chantiers actifs — coût horaire</h3>
              <ul className="space-y-2">
                {dash.chantiers_state.map(c => {
                  const overrun = c.overrun_pct;
                  const overBudget = overrun != null && overrun > 0;
                  return (
                    <li key={c.id} className="p-3 rounded-lg bg-white/[0.02] border border-atlasBorder">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-white font-medium truncate">{c.title}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${c.status === 'en_cours' ? 'bg-atlasCyan/15 text-atlasCyan' : 'bg-white/5 text-atlasTextSecondary'}`}>{c.status}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-atlasTextSecondary">{c.actual_hours} h réelles / {c.estimated_hours} h estimées</span>
                        {overrun != null && (
                          <span className={overBudget ? 'text-orange-400 font-semibold' : 'text-atlasCyan'}>
                            {overBudget ? `+${overrun}% dépassement` : `${overrun}% du budget`}
                          </span>
                        )}
                      </div>
                      {overBudget && (
                        <div className="mt-2 bg-orange-500/10 border border-orange-500/30 rounded-md p-2">
                          <p className="text-[11px] text-orange-300">⚠ Le chantier dépasse l'estimation. Vérifie avec le chef d'équipe ce qui bloque.</p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* === WORKERS === */}
      {tab === 'workers' && (
        <div className="space-y-6">
          <div className="glass-card p-6 space-y-3">
            <h3 className="text-lg font-semibold text-white">Nouvel ouvrier</h3>
            <div className="grid md:grid-cols-4 gap-3">
              <div className="md:col-span-2"><Label className="text-atlasTextSecondary text-sm">Nom complet</Label>
                <Input data-testid="worker-name" value={newWorker.name} onChange={e => setNewWorker({...newWorker, name: e.target.value})} className="input-atlas mt-1" placeholder="Marco Dupont" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Rôle</Label>
                <select value={newWorker.role} onChange={e => setNewWorker({...newWorker, role: e.target.value})} className="input-atlas mt-1 w-full px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white">
                  <option value="ouvrier">Ouvrier</option>
                  <option value="chef">Chef d'équipe</option>
                </select>
              </div>
              <div><Label className="text-atlasTextSecondary text-sm">Coût horaire (€)</Label>
                <Input type="number" step="0.5" value={newWorker.hourly_cost} onChange={e => setNewWorker({...newWorker, hourly_cost: e.target.value})} className="input-atlas mt-1" placeholder="18" /></div>
            </div>
            <Button data-testid="worker-create" onClick={createWorker} className="btn-primary"><Plus className="w-4 h-4 mr-1" />Créer + générer PIN</Button>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Mon équipe ({workers.length})</h3>
            {workers.length === 0 ? (
              <p className="text-atlasTextSecondary text-sm text-center py-6">Aucun ouvrier. Crée le premier ci-dessus.</p>
            ) : (
              <ul className="divide-y divide-atlasBorder">
                {workers.map(w => (
                  <li key={w.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white font-medium flex items-center gap-2">{w.name}
                        {w.role === 'chef' && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-atlasCyan/15 text-atlasCyan">Chef</span>}
                        {!w.active && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">Désactivé</span>}
                        {w.device_bound && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-atlasCyan/10 text-atlasCyan flex items-center gap-1"><Smartphone className="w-2.5 h-2.5" />Lié</span>}
                      </p>
                      <p className="text-xs text-atlasTextSecondary">{w.hourly_cost ? `${w.hourly_cost}€/h · ` : ''}créé le {new Date(w.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => regenPin(w)} className="text-xs text-atlasCyan hover:bg-atlasCyan/10 px-2 py-1 rounded flex items-center gap-1"><RefreshCw className="w-3 h-3" />PIN</button>
                      {w.device_bound && (
                        <button data-testid={`reset-device-${w.id}`} onClick={() => resetDevice(w)} className="text-xs text-orange-400 hover:bg-orange-500/10 px-2 py-1 rounded flex items-center gap-1" title="Réinitialiser l'appareil lié"><Smartphone className="w-3 h-3" />Reset</button>
                      )}
                      <button onClick={() => toggleWorker(w)} className="text-xs text-atlasTextSecondary hover:text-white px-2 py-1 rounded">{w.active ? 'Désactiver' : 'Activer'}</button>
                      <button onClick={() => deleteWorker(w)} className="text-red-400 hover:bg-red-500/10 p-1.5 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* === CHANTIERS === */}
      {tab === 'chantiers' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Mes chantiers ({chantiers.length})</h3>
            <Button data-testid="chantier-new-btn" onClick={() => setShowChForm(s => !s)} className="btn-primary"><Plus className="w-4 h-4 mr-1" />{showChForm ? 'Fermer' : 'Nouveau chantier'}</Button>
          </div>

          {showChForm && (
            <div className="glass-card p-6 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-atlasTextSecondary text-sm">Titre</Label>
                  <Input data-testid="ch-title" value={chForm.title} onChange={e => setChForm({...chForm, title: e.target.value})} className="input-atlas mt-1" placeholder="Toiture Etienne — Verviers" /></div>
                <div><Label className="text-atlasTextSecondary text-sm">Client</Label>
                  <Input value={chForm.client_name} onChange={e => setChForm({...chForm, client_name: e.target.value})} className="input-atlas mt-1" /></div>
                <div><Label className="text-atlasTextSecondary text-sm">Adresse</Label>
                  <Input value={chForm.address} onChange={e => setChForm({...chForm, address: e.target.value})} className="input-atlas mt-1" /></div>
                <div><Label className="text-atlasTextSecondary text-sm">Type</Label>
                  <select value={chForm.type_chantier} onChange={e => setChForm({...chForm, type_chantier: e.target.value})} className="input-atlas mt-1 w-full px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white">
                    {TYPE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select></div>
                <div><Label className="text-atlasTextSecondary text-sm">GPS latitude</Label>
                  <Input value={chForm.gps_lat} onChange={e => setChForm({...chForm, gps_lat: e.target.value})} className="input-atlas mt-1" placeholder="50.589600" /></div>
                <div><Label className="text-atlasTextSecondary text-sm">GPS longitude</Label>
                  <Input value={chForm.gps_lng} onChange={e => setChForm({...chForm, gps_lng: e.target.value})} className="input-atlas mt-1" placeholder="5.862500" /></div>
                <div className="md:col-span-2">
                  <button type="button" onClick={useCurrentLocationForChantier} className="text-xs text-atlasCyan hover:underline flex items-center gap-1"><MapPin className="w-3 h-3" />Utiliser ma position actuelle</button>
                </div>
                <div><Label className="text-atlasTextSecondary text-sm">Date de début</Label>
                  <Input type="date" value={chForm.date_debut} onChange={e => setChForm({...chForm, date_debut: e.target.value})} className="input-atlas mt-1" /></div>
                <div><Label className="text-atlasTextSecondary text-sm">Heures estimées</Label>
                  <Input type="number" step="0.5" value={chForm.estimated_hours} onChange={e => setChForm({...chForm, estimated_hours: e.target.value})} className="input-atlas mt-1" placeholder="80" /></div>
              </div>

              {/* Jours travaillés */}
              <div>
                <Label className="text-atlasTextSecondary text-sm mb-2 block">Jours de travail sur ce chantier</Label>
                <div className="flex flex-wrap gap-2" data-testid="ch-working-days">
                  {[
                    { v: 0, l: 'Lun' }, { v: 1, l: 'Mar' }, { v: 2, l: 'Mer' }, { v: 3, l: 'Jeu' },
                    { v: 4, l: 'Ven' }, { v: 5, l: 'Sam' }, { v: 6, l: 'Dim' },
                  ].map(d => {
                    const active = chForm.working_days.includes(d.v);
                    return (
                      <button key={d.v} type="button" data-testid={`day-toggle-${d.v}`}
                        onClick={() => setChForm(f => ({
                          ...f,
                          working_days: active ? f.working_days.filter(x => x !== d.v) : [...f.working_days, d.v].sort(),
                        }))}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${active ? 'bg-atlasCyan/15 border-atlasCyan/50 text-atlasCyan' : 'bg-white/[0.02] border-atlasBorder text-atlasTextSecondary hover:border-atlasCyan/30'}`}>
                        {d.l}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-atlasTextSecondary mt-2">Coche les jours où ton équipe bosse normalement sur ce chantier. Par défaut : L-V.</p>
              </div>

              {/* Dates exceptionnelles */}
              <div>
                <Label className="text-atlasTextSecondary text-sm mb-2 block">Dates exceptionnelles (ex: dimanche ponctuel)</Label>
                <div className="flex items-center gap-2">
                  <Input type="date" value={newExDate} onChange={e => setNewExDate(e.target.value)} className="input-atlas flex-1" />
                  <Button type="button" data-testid="ch-add-exdate" onClick={() => {
                    if (!newExDate) return;
                    if (chForm.exceptional_dates.includes(newExDate)) { toast.info('Déjà ajoutée'); return; }
                    setChForm(f => ({ ...f, exceptional_dates: [...f.exceptional_dates, newExDate].sort() }));
                    setNewExDate('');
                  }} variant="outline" className="border-atlasCyan/40 text-atlasCyan"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
                </div>
                {chForm.exceptional_dates.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {chForm.exceptional_dates.map(d => (
                      <span key={d} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs">
                        {new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        <button type="button" onClick={() => setChForm(f => ({...f, exceptional_dates: f.exceptional_dates.filter(x => x !== d)}))} className="hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-atlasTextSecondary text-sm mb-2 block">Équipe affectée</Label>
                {workers.length === 0 ? (
                  <p className="text-xs text-atlasTextSecondary">Crée d'abord des ouvriers dans l'onglet Ouvriers.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {workers.filter(w => w.active).map(w => {
                      const assigned = chForm.assigned_worker_ids.includes(w.id);
                      const isChef = chForm.chef_id === w.id;
                      return (
                        <div key={w.id} className={`p-3 rounded-lg border cursor-pointer transition ${assigned ? 'bg-atlasCyan/10 border-atlasCyan/40' : 'bg-white/[0.02] border-atlasBorder hover:border-atlasCyan/30'}`} onClick={() => toggleAssign(w.id)}>
                          <p className="text-sm text-white font-medium">{w.name}</p>
                          {assigned && (
                            <label className="flex items-center gap-1 mt-1 text-[11px] text-atlasTextSecondary cursor-pointer" onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={isChef} onChange={() => setChForm(f => ({...f, chef_id: isChef ? '' : w.id}))} />
                              Désigner chef d'équipe
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Button data-testid="ch-save" onClick={createChantier} className="btn-primary w-full">Créer le chantier</Button>
            </div>
          )}

          <ul className="space-y-3">
            {chantiers.map(c => {
              const overrun = c.estimated_hours > 0 ? (c.actual_hours / c.estimated_hours * 100 - 100) : null;
              return (
                <li key={c.id} className="glass-card p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate flex items-center gap-2">
                        {c.title}
                        {c.checklist_progress?.validated && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Checklist ✓</span>}
                      </p>
                      <p className="text-xs text-atlasTextSecondary">{c.client_name || '—'} · {TYPE_OPTIONS.find(o => o.v === c.type_chantier)?.l || c.type_chantier || 'Autre'}</p>
                      {c.checklist_progress && c.checklist_progress.total > 0 && !c.checklist_progress.validated && (
                        <p className="text-[11px] text-atlasTextSecondary mt-1">
                          Qualité : {c.checklist_progress.done}/{c.checklist_progress.total} items
                          {c.checklist_progress.complete ? ' — en attente validation chef' : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={c.status} onChange={e => updateChStatus(c, e.target.value)} className="text-xs bg-atlasBg border border-atlasBorder text-white rounded px-2 py-1">
                        <option value="planifie">Planifié</option>
                        <option value="en_cours">En cours</option>
                        <option value="termine">Terminé</option>
                        <option value="archive">Archivé</option>
                      </select>
                      <button onClick={() => deleteChantier(c)} className="text-red-400 hover:bg-red-500/10 p-1.5 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div><span className="text-atlasTextSecondary">Heures</span><p className={`font-semibold ${overrun != null && overrun > 0 ? 'text-orange-400' : 'text-white'}`}>{c.actual_hours} / {c.estimated_hours} h</p></div>
                    <div><span className="text-atlasTextSecondary">Équipe</span><p className="text-white">{(c.assigned_workers || []).map(w => w.name).join(', ') || '—'}</p></div>
                    <div>
                      <span className="text-atlasTextSecondary">Jours</span>
                      <p className="text-white text-[11px]">
                        {(() => {
                          const wd = c.working_days || [0,1,2,3,4];
                          const labels = ['L','M','M','J','V','S','D'];
                          const days = labels.map((l, i) => wd.includes(i) ? <strong key={i} className="text-atlasCyan">{l}</strong> : <span key={i} className="text-atlasTextSecondary/40">{l}</span>);
                          return <>{days.reduce((p, d, i) => p ? [...p, ' ', d] : [d], null)}</>;
                        })()}
                        {(c.exceptional_dates || []).length > 0 && <span className="text-orange-400 ml-1">+{c.exceptional_dates.length}j</span>}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
            {chantiers.length === 0 && <p className="text-atlasTextSecondary text-sm text-center py-6">Aucun chantier créé.</p>}
          </ul>
        </div>
      )}

      {/* PIN reveal modal */}
      {pinModal && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={() => setPinModal(null)}>
          <div className="glass-card p-6 max-w-sm w-full border border-atlasCyan/40" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPinModal(null)} className="float-right text-atlasTextSecondary hover:text-white"><X className="w-5 h-5" /></button>
            <p className="text-atlasTextSecondary text-sm">PIN pour</p>
            <p className="text-white text-xl font-semibold mb-4">{pinModal.name}</p>
            <div className="text-center bg-atlasCyan/10 border-2 border-atlasCyan/40 rounded-xl py-6 mb-3">
              <p className="text-5xl font-bold text-atlasCyan tracking-[0.4em] font-mono">{pinModal.pin}</p>
            </div>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-3">
              <p className="text-[12px] text-orange-300 flex items-start gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />Note ce PIN maintenant. Pour des raisons de sécurité, ATLAS ne le ré-affichera plus.</p>
            </div>
            <Button onClick={async () => { await navigator.clipboard.writeText(pinModal.pin); toast.success('PIN copié'); }} className="btn-primary w-full">Copier le PIN</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Team;
